import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { slugify } from '../utils';
import { BACKEND_URL } from '../config';
import FormationForm, { FormationFormData } from '../components/forms/FormationForm';
import PageMeta from '../components/PageMeta';

export default function NewFormation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isStudy: boolean = location.state?.createStudy !== false;
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (data: FormationFormData & { logoFile: File | null }) => {
    const token = localStorage.getItem('token');
    if (!token) return navigate(-1);
    setSubmitting(true);

    const strid = slugify(data.name);
    const formData = new FormData();
    formData.append('strid', strid);
    formData.append('isStudy', String(data.isStudy));
    formData.append('name', data.name);
    formData.append('localisation', data.localisation);
    formData.append('position', JSON.stringify(data.position));
    formData.append('tasks', JSON.stringify(data.tasks));
    formData.append('courses', JSON.stringify(data.courses));
    formData.append('start_date', data.startDate);
    formData.append('end_date', data.isOngoing ? 'null' : data.endDate);
    formData.append('ongoing', String(data.isOngoing));
    formData.append('degree', JSON.stringify(data.degree));
    formData.append('type', JSON.stringify(data.type));
    formData.append('private_links', JSON.stringify(data.privateLinks.filter(l => l.url.trim())));
    data.manualSkillIds.forEach((id, i) => formData.append(`skills[${i}]`, String(id)));
    data.selectedProjectIds.forEach((id, i) => formData.append(`projects[${i}]`, String(id)));
    if (data.logoFile) formData.append('logo', data.logoFile);

    try {
      const res = await fetch(`${BACKEND_URL}/api/formations`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) navigate('/formations', { replace: true });
      else toast.error(String(t('error_saving')));
    } catch {
      toast.error(String(t('error_saving')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10 py-6 sm:py-10 px-4 pt-24">
      <PageMeta title={t('create_formation') as string} />
      <div className="max-w-6xl mx-auto">
        <Link
          to="/formations"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-primary-light transition-colors mb-5 group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
          {t('formations')}
        </Link>
        <h1 className="font-display font-extrabold tracking-[-0.03em] text-4xl text-ink mb-8">{t('create_formation')}</h1>
        <FormationForm
          mode="new"
          isStudy={isStudy}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      </div>
    </div>
  );
}
